import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle, Circle, ArrowRight, Rocket, X, Confetti,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// A friendly, data-driven "get started" checklist for coaches. Every step is
// computed from real state (roster/program/profile) so it stays honest and
// ticks itself off as the coach actually does the work — no manual marking.
export default function CoachQuickStart({ data }) {
  const { user, setUser } = useAuth();
  const [dismissing, setDismissing] = useState(false);

  const steps = useMemo(() => [
    {
      key: "profile",
      label: "Set up your coach profile",
      desc: "Tell athletes who you are and what you specialise in.",
      done: !!user?.coach_onboarded_at,
      to: "/profile",
      cta: "EDIT PROFILE",
    },
    {
      key: "client",
      label: "Invite your first athlete",
      desc: "Share your join link and start building your roster.",
      done: (data?.total_clients || 0) > 0,
      to: "/coach/clients",
      cta: "INVITE ATHLETE",
    },
    {
      key: "program",
      label: "Build a training program",
      desc: "Create a reusable multi-week program to assign.",
      done: (data?.total_programs || 0) > 0,
      to: "/coach/programs",
      cta: "BUILD PROGRAM",
    },
    {
      key: "assign",
      label: "Assign a program to an athlete",
      desc: "Put a plan in an athlete's hands and track progress.",
      done: (data?.active_assignments || 0) > 0,
      to: "/coach/clients",
      cta: "ASSIGN NOW",
    },
    {
      key: "public",
      label: "Publish your public coach page",
      desc: "Get a shareable page to attract new clients.",
      done: !!user?.coach_slug,
      to: "/profile",
      cta: "GET MY PAGE",
    },
  ], [data, user]);

  const doneCount = steps.filter((s) => s.done).length;
  const total = steps.length;
  const pct = Math.round((doneCount / total) * 100);
  const allDone = doneCount === total;
  const dismissed = !!user?.onboarding_dismissed?.coach_quickstart;

  const dismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    // Optimistic — hide immediately, persist in background.
    setUser((u) => ({
      ...u,
      onboarding_dismissed: { ...(u?.onboarding_dismissed || {}), coach_quickstart: true },
    }));
    try {
      await api.post("/onboarding/hint/dismiss", { key: "coach_quickstart" });
    } catch { /* non-blocking */ }
  };

  // Hide once the coach dismisses it (they can always re-run the tour).
  if (dismissed) return null;

  // The next unfinished step gets the spotlight.
  const nextStep = steps.find((s) => !s.done);

  return (
    <div
      className="tactical-card p-5 mb-8 relative overflow-hidden"
      data-testid="coach-quickstart"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.07]"
        style={{ background: "radial-gradient(circle at 15% 0%, var(--brand-accent, #FF6B35), transparent 55%)" }}
      />
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 text-text-secondary hover:text-white transition-colors"
        data-testid="quickstart-dismiss"
        aria-label="Dismiss checklist"
      >
        <X size={16} weight="bold" />
      </button>

      <div className="relative">
        <div className="flex items-center gap-2 mb-1">
          {allDone ? (
            <Confetti size={16} weight="fill" className="text-heat" />
          ) : (
            <Rocket size={16} weight="fill" className="text-heat" />
          )}
          <span className="label-tiny text-brand-accent">
            {allDone ? "// YOU'RE ALL SET" : "// GET STARTED"}
          </span>
        </div>
        <h2 className="font-heading text-2xl mb-1">
          {allDone ? "YOUR COACHING SETUP IS COMPLETE" : "SET UP YOUR COACHING IN 5 STEPS"}
        </h2>
        <p className="text-xs text-text-secondary mb-4">
          {allDone
            ? "Nice work — every essential is in place. Dismiss this card to clear it."
            : "Knock these out to get the most out of Edge. It ticks itself off as you go."}
        </p>

        {/* Progress bar */}
        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-2 bg-white/10 overflow-hidden rounded-full">
            <div
              className="h-full bg-heat transition-all duration-500 rounded-full"
              style={{ width: `${Math.max(4, pct)}%` }}
              data-testid="quickstart-progress-bar"
            />
          </div>
          <span className="label-tiny text-heat tabular-nums" data-testid="quickstart-progress">
            {doneCount}/{total}
          </span>
        </div>

        {allDone ? (
          <button
            onClick={dismiss}
            className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest"
            data-testid="quickstart-done-cta"
          >
            CLEAR THIS CARD
          </button>
        ) : (
          <div className="space-y-2">
            {steps.map((s) => (
              <div
                key={s.key}
                className={`flex items-center gap-3 border p-3 transition-colors ${
                  s.done
                    ? "border-green-500/20 bg-green-500/[0.03]"
                    : s.key === nextStep?.key
                    ? "border-heat/40 bg-heat/[0.04]"
                    : "border-slate-200"
                }`}
                data-testid={`quickstart-step-${s.key}`}
              >
                {s.done ? (
                  <CheckCircle size={20} weight="fill" className="text-green-500 shrink-0" />
                ) : (
                  <Circle size={20} weight="duotone" className="text-text-secondary/50 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-heading ${s.done ? "line-through text-text-secondary" : ""}`}>
                    {s.label}
                  </div>
                  {!s.done && (
                    <div className="text-[11px] text-text-secondary mt-0.5">{s.desc}</div>
                  )}
                </div>
                {!s.done && (
                  <Link
                    to={s.to}
                    className="shrink-0 inline-flex items-center gap-1 label-tiny text-brand-accent border border-brand-accent/40 px-2.5 py-1.5 hover:bg-brand-accent hover:text-surface-base transition-colors"
                    data-testid={`quickstart-cta-${s.key}`}
                  >
                    {s.cta} <ArrowRight size={11} weight="bold" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
